import { useEscClose } from '../hooks/useEscClose';
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import {
  Sparkles, Play, RefreshCw, CheckCircle2, XCircle, AlertCircle,
  Loader2, ChevronRight, ChevronDown, Eye, ArrowLeft, FileText,
  Zap, Clock, Rocket
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

/* -------------------------------------------------------------------------- */
/* Small UI helpers                                                            */
/* -------------------------------------------------------------------------- */

const StatusPill = ({ status }) => {
  const map = {
    approved: { label: 'Freigegeben', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
    pending:  { label: 'Prüfung offen', cls: 'bg-amber-100 text-amber-900 border-amber-300' },
    rejected: { label: 'Abgelehnt', cls: 'bg-rose-100 text-rose-800 border-rose-300' },
    missing:  { label: 'Nicht generiert', cls: 'bg-cream-200 text-espresso/60 border-cream-300' },
  };
  const s = map[status] || map.missing;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${s.cls}`}
          data-testid={`enh-status-${status}`}>
      {s.label}
    </span>
  );
};

const StatCard = ({ label, value, hint, color = "espresso", testid }) => (
  <div className="bg-cream-100 rounded-xl border border-cream-300 px-4 py-3 min-w-[140px]" data-testid={testid}>
    <div className="text-[10.5px] uppercase tracking-widest font-semibold text-espresso/50">{label}</div>
    <div className={`font-display text-3xl mt-1 text-${color}`}>{value}</div>
    {hint && <div className="text-[11px] text-espresso/60 mt-0.5">{hint}</div>}
  </div>
);

/* -------------------------------------------------------------------------- */
/* Diff Viewer                                                                 */
/* -------------------------------------------------------------------------- */

const DiffViewer = ({ moduleId, onClose, onActionDone }) => {
  useEscClose(onClose);
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/enhance/module/${moduleId}/diff`, getAuthHeader());
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Konnte Diff nicht laden');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [moduleId]);

  const doAction = async (action, extra = {}) => {
    setBusy(action);
    try {
      if (action === 'approve') {
        await axios.post(`${API}/admin/enhance/module/${moduleId}/approve`, {}, getAuthHeader());
        toast.success('Modul freigegeben – wird jetzt live ausgeliefert');
      } else if (action === 'reject') {
        const reason = window.prompt('Grund der Ablehnung (optional):') || '';
        await axios.post(`${API}/admin/enhance/module/${moduleId}/reject`, { reason }, getAuthHeader());
        toast.success('Modul abgelehnt');
      } else if (action === 'regenerate') {
        if (!window.confirm('Wirklich neu generieren? Die aktuelle LLM-Version wird ersetzt.')) {
          setBusy(null); return;
        }
        await axios.post(
          `${API}/admin/enhance/module/${moduleId}?force=true&model=gpt-4o`,
          {},
          getAuthHeader()
        );
        toast.success('Neu generiert');
      } else if (action === 'delete') {
        if (!window.confirm('LLM-Version wirklich löschen?')) { setBusy(null); return; }
        await axios.delete(`${API}/admin/enhance/module/${moduleId}`, getAuthHeader());
        toast.success('Gelöscht');
        onActionDone?.();
        onClose();
        return;
      }
      await load();
      onActionDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Aktion fehlgeschlagen');
    }
    setBusy(null);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-espresso/50 z-50 flex items-center justify-center" data-testid="diff-loading">
        <Loader2 className="w-8 h-8 text-cream-50 animate-spin" />
      </div>
    );
  }
  if (!data) return null;

  const factor = data.original_word_count
    ? (data.enhanced_word_count / data.original_word_count).toFixed(1)
    : '∞';

  return (
    <div className="fixed inset-0 bg-espresso/70 z-50 flex flex-col" role="dialog" aria-modal="true" aria-label="Inhalts-Vergleich" data-testid="diff-viewer">
      {/* Header */}
      <div className="bg-cream-50 border-b border-cream-300 px-6 py-4 flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <button onClick={onClose}
                  className="inline-flex items-center gap-1.5 text-[12px] text-espresso/60 hover:text-espresso mb-1"
                  data-testid="diff-back-btn">
            <ArrowLeft className="w-3.5 h-3.5" /> Zurück zur Übersicht
          </button>
          <div className="flex items-center gap-2.5 flex-wrap">
            <h2 className="font-display text-2xl text-espresso truncate">{data.module_title}</h2>
            <StatusPill status={data.approval_status} />
          </div>
          <div className="text-[12px] text-espresso/60 mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
            <span>Original: {data.original_word_count.toLocaleString('de-DE')} Wörter</span>
            <span>LLM: <strong className="text-terra">{data.enhanced_word_count.toLocaleString('de-DE')} Wörter</strong></span>
            <span>Faktor: <strong>{factor}×</strong></span>
            <span>Modell: {data.model}</span>
            {data.generated_at && <span>Generiert: {new Date(data.generated_at).toLocaleString('de-DE')}</span>}
            {data.approved_at && <span className="text-emerald-700">✓ Freigegeben von {data.approved_by}</span>}
            {data.rejected_at && <span className="text-rose-700">✗ Abgelehnt von {data.rejected_by}</span>}
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button
            disabled={!!busy || data.approval_status === 'approved'}
            onClick={() => doAction('approve')}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="diff-approve-btn">
            {busy === 'approve' ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Freigeben
          </button>
          <button
            disabled={!!busy || data.approval_status === 'rejected'}
            onClick={() => doAction('reject')}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="diff-reject-btn">
            {busy === 'reject' ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
            Ablehnen
          </button>
          <button
            disabled={!!busy}
            onClick={() => doAction('regenerate')}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-cream-200 text-espresso text-sm font-medium hover:bg-cream-300 disabled:opacity-40"
            data-testid="diff-regen-btn">
            {busy === 'regenerate' ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Neu generieren
          </button>
        </div>
      </div>

      {/* Side-by-side content */}
      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-2 gap-0 bg-cream-100">
        <div className="border-r border-cream-300 flex flex-col min-h-0">
          <div className="bg-cream-200/80 px-5 py-2.5 border-b border-cream-300 flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-widest font-semibold text-espresso/60">Original</span>
            <span className="text-[11px] text-espresso/50">{data.original_word_count} Wörter</span>
          </div>
          <div className="flex-1 overflow-y-auto p-5 bg-cream-50">
            <div className="prose prose-sm max-w-none prose-headings:font-display prose-headings:text-espresso prose-p:text-espresso/80">
              {data.original_content
                ? <ReactMarkdown>{data.original_content}</ReactMarkdown>
                : <div className="text-espresso/40 italic">Kein Ausgangs-Inhalt vorhanden.</div>}
            </div>
          </div>
        </div>
        <div className="flex flex-col min-h-0">
          <div className="bg-terra/10 px-5 py-2.5 border-b border-terra/30 flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-widest font-semibold text-terra flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> LLM-Version
            </span>
            <span className="text-[11px] text-espresso/70">{data.enhanced_word_count} Wörter · {factor}×</span>
          </div>
          <div className="flex-1 overflow-y-auto p-5 bg-cream-100">
            <div className="prose prose-sm max-w-none prose-headings:font-display prose-headings:text-espresso prose-p:text-espresso/80">
              <ReactMarkdown>{data.enhanced_content}</ReactMarkdown>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Course drill-down                                                          */
/* -------------------------------------------------------------------------- */

const CoursePanel = ({ course, onOpenDiff, onRefresh }) => {
  const { getAuthHeader } = useAuth();
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState(null);
  const [starting, setStarting] = useState(false);
  const [enhancingId, setEnhancingId] = useState(null);

  const loadSummary = async () => {
    try {
      const res = await axios.get(`${API}/admin/enhance/course/${course.course_id}/summary`, getAuthHeader());
      setSummary(res.data);
    } catch {
      toast.error(`${course.course_title}: konnte Details nicht laden`);
    }
  };

  const toggle = () => {
    if (!open && !summary) loadSummary();
    setOpen(v => !v);
  };

  const startCourse = async (force = false) => {
    setStarting(true);
    try {
      const res = await axios.post(
        `${API}/admin/enhance/course/${course.course_id}/start?model=gpt-4o&force=${force}`,
        {}, getAuthHeader()
      );
      toast.success(`Job gestartet für ${course.course_title}`);
      onRefresh?.(res.data.job_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Konnte Job nicht starten');
    }
    setStarting(false);
  };

  const enhanceOne = async (mid, force) => {
    setEnhancingId(mid);
    try {
      await axios.post(
        `${API}/admin/enhance/module/${mid}?model=gpt-4o&force=${force ? 'true' : 'false'}`,
        {}, getAuthHeader()
      );
      toast.success('Modul generiert');
      await loadSummary();
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generierung fehlgeschlagen');
    }
    setEnhancingId(null);
  };

  const previewPdf = async () => {
    const toastId = toast.loading('PDF-Generierung gestartet …');
    try {
      const startRes = await axios.post(
        `${API}/admin/course-script/${course.course_id}/start?include_pending=true`,
        {}, getAuthHeader()
      );
      const jobId = startRes.data.job_id;
      for (let i = 0; i < 400; i++) {
        await new Promise(r => setTimeout(r, 1500));
        const st = (await axios.get(
          `${API}/user/course-script/status/${jobId}`, getAuthHeader()
        )).data;
        if (st.status === 'done') {
          toast.loading('PDF wird geladen …', { id: toastId });
          const res = await axios.get(
            `${API}/user/course-script/download/${jobId}`,
            { ...getAuthHeader(), responseType: 'blob' }
          );
          const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
          window.open(url, '_blank');
          toast.success('Preview-PDF geöffnet', { id: toastId });
          return;
        }
        if (st.status === 'error') throw new Error(st.error || 'Generierung fehlgeschlagen');
        toast.loading(
          st.status === 'rendering_pdf'
            ? 'Layout & Rendering …'
            : `Lade Inhalte … Modul ${st.current_module}/${st.total_modules} (${st.progress_percent}%)`,
          { id: toastId }
        );
      }
      throw new Error('Zeitüberschreitung');
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Preview fehlgeschlagen', { id: toastId });
    }
  };

  const approveAll = async () => {
    if (!window.confirm(
      `Wirklich ALLE ${course.pending} offenen Module dieses Kurses freigeben?\n\n` +
      `Danach erscheinen sie sofort für die Teilnehmer im PDF-Skript.`
    )) return;
    try {
      const res = await axios.post(
        `${API}/admin/enhance/course/${course.course_id}/approve-all`,
        {}, getAuthHeader()
      );
      toast.success(`${res.data.approved_count} Module freigegeben`);
      await loadSummary();
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Freigabe fehlgeschlagen');
    }
  };

  const donePct = course.total_modules
    ? Math.round(100 * (course.approved) / course.total_modules)
    : 0;

  return (
    <div className="bg-cream-50 border border-cream-300 rounded-xl overflow-hidden" data-testid={`course-panel-${course.course_id}`}>
      {/* Toggle row — use a div (not a nested button) so we can put action buttons inside */}
      <div
        role="button"
        tabIndex={0}
        onClick={toggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-cream-100 transition text-left cursor-pointer select-none"
        data-testid={`course-toggle-${course.course_id}`}>
        {open ? <ChevronDown className="w-4 h-4 text-espresso/60 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-espresso/60 flex-shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="font-medium text-espresso truncate">{course.course_title}</div>
          <div className="text-[11.5px] text-espresso/60 mt-0.5">{course.total_modules} Module</div>
        </div>
        {/* EIN klares Status-Wort mit Punkt */}
        <div className="flex items-center gap-2 flex-shrink-0 mr-1" data-testid={`course-status-${course.course_id}`}>
          {(() => {
            const st = course.pending > 0
              ? { dot: '#E07A5F', label: 'Prüfung offen' }
              : course.approved === course.total_modules && course.total_modules > 0
                ? { dot: '#D89232', label: `${donePct} % Live` }
                : course.approved > 0
                  ? { dot: '#D89232', label: `${donePct} % Live` }
                  : { dot: '#B4AA9C', label: 'Nicht generiert' };
            return (
              <>
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: st.dot }} />
                <span className="font-mono text-[10px] uppercase font-semibold" style={{ letterSpacing: '0.14em', color: st.dot === '#B4AA9C' ? '#8A8175' : st.dot }}>
                  {st.label}
                </span>
              </>
            );
          })()}
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); previewPdf(); }}
          className="ml-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cream-100 text-espresso text-[12px] font-medium hover:bg-cream-200 border border-cream-300"
          data-testid={`preview-pdf-${course.course_id}`}
          title="Skript-PDF mit LLM-Inhalt als Vorschau öffnen">
          <FileText className="w-3.5 h-3.5" />
          Preview-PDF
        </button>
        {course.pending > 0 && (
          <button
            onClick={(e) => { e.stopPropagation(); approveAll(); }}
            className="ml-1 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-[0.08em] transition-colors"
            style={{ border: '1.5px solid #E07A5F', color: '#E07A5F' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#E07A5F'; e.currentTarget.style.color = '#F5F0EA'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#E07A5F'; }}
            data-testid={`approve-all-${course.course_id}`}>
            <CheckCircle2 className="w-3.5 h-3.5" />
            Alle {course.pending} freigeben
          </button>
        )}
        {course.missing > 0 && (
          <button
            onClick={(e) => { e.stopPropagation(); startCourse(false); }}
            disabled={starting}
            className="ml-1 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-terra text-cream-50 text-[12px] font-medium hover:bg-terra/90 disabled:opacity-50"
            data-testid={`start-course-${course.course_id}`}>
            {starting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
            {course.missing} fehlende generieren
          </button>
        )}
      </div>

      {open && (
        <div className="border-t border-cream-300 bg-cream-100">
          {!summary && (
            <div className="p-4 flex items-center gap-2 text-espresso/60 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Lade Modul-Details…
            </div>
          )}
          {summary && (
            <div className="divide-y divide-cream-200">
              {summary.modules.map(m => (
                <div key={m.module_id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-cream-100/50">
                  <div className="w-8 text-[11px] text-espresso/40 tabular-nums text-right">{m.order}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-espresso truncate">{m.module_title}</div>
                    <div className="text-[11px] text-espresso/50 mt-0.5">
                      {m.approval_status !== 'missing'
                        ? <>Original {m.original_word_count} → LLM <strong>{m.enhanced_word_count}</strong> Wörter</>
                        : 'Noch nicht generiert'}
                    </div>
                  </div>
                  <StatusPill status={m.approval_status} />
                  {m.approval_status === 'missing' ? (
                    <button
                      onClick={() => enhanceOne(m.module_id, false)}
                      disabled={enhancingId === m.module_id}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-terra text-cream-50 text-[11px] font-medium hover:bg-terra/90 disabled:opacity-50"
                      data-testid={`gen-module-${m.module_id}`}>
                      {enhancingId === m.module_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                      Generieren
                    </button>
                  ) : (
                    <button
                      onClick={() => onOpenDiff(m.module_id)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-cream-100 text-espresso text-[11px] font-medium hover:bg-cream-200 border border-cream-300"
                      data-testid={`review-module-${m.module_id}`}>
                      <Eye className="w-3 h-3" /> Prüfen
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Job progress card                                                          */
/* -------------------------------------------------------------------------- */

const JobProgressCard = ({ jobId, onClose, onRefresh }) => {
  const { getAuthHeader } = useAuth();
  const [job, setJob] = useState(null);
  const timerRef = useRef(null);

  const poll = async () => {
    try {
      const res = await axios.get(`${API}/admin/enhance/status/${jobId}`, getAuthHeader());
      setJob(res.data);
      if (res.data.status === 'done' || res.data.status === 'error') {
        clearInterval(timerRef.current);
        onRefresh?.();
      }
    } catch {
      clearInterval(timerRef.current);
    }
  };

  useEffect(() => {
    poll();
    timerRef.current = setInterval(poll, 3500);
    return () => clearInterval(timerRef.current);
  }, [jobId]);

  if (!job) return null;

  const mm = Math.floor(job.elapsed_seconds / 60);
  const ss = Math.floor(job.elapsed_seconds % 60).toString().padStart(2, '0');

  return (
    <div className="bg-espresso text-cream-50 rounded-xl p-5 mb-4 shadow-lg" data-testid="job-progress">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {job.status === 'done' ? <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            : job.status === 'error' ? <AlertCircle className="w-5 h-5 text-rose-400" />
            : <Loader2 className="w-5 h-5 animate-spin text-terra" />}
          <div>
            <div className="font-display text-lg">
              {job.job_type === 'all' ? 'Alle Kurse: LLM-Generierung' : `Kurs: ${job.course_title}`}
            </div>
            <div className="text-[11.5px] text-cream-50/60">
              Status: {job.status} · Laufzeit {mm}:{ss}
              {job.total_modules > 0 && <> · Modul {job.current_module}/{job.total_modules}</>}
            </div>
          </div>
        </div>
        {(job.status === 'done' || job.status === 'error') && (
          <button onClick={onClose} className="text-cream-50/60 hover:text-cream-50 text-xs"
                  data-testid="job-close-btn">Schließen</button>
        )}
      </div>

      {job.current_module_title && job.status === 'running' && (
        <div className="text-[12px] text-cream-50/70 mb-2 truncate">
          → {job.current_course_title ? `${job.current_course_title}: ` : ''}{job.current_module_title}
        </div>
      )}

      <div className="w-full h-2 bg-cream-50/10 rounded-full overflow-hidden">
        <div className="h-full bg-terra transition-all duration-500" style={{ width: `${job.progress_percent}%` }} />
      </div>
      <div className="flex justify-between text-[11px] text-cream-50/60 mt-2">
        <span>{job.progress_percent}%</span>
        <span>
          <span className="text-emerald-400">{job.completed_count} fertig</span>
          {job.error_count > 0 && <> · <span className="text-rose-400">{job.error_count} Fehler</span></>}
          {job.total_new_words_added > 0 && <> · +{job.total_new_words_added.toLocaleString('de-DE')} Wörter</>}
        </span>
      </div>

      {job.error && (
        <div className="mt-3 text-[12px] text-rose-300 bg-rose-900/30 rounded-lg p-2">
          Fehler: {job.error}
        </div>
      )}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Main page                                                                  */
/* -------------------------------------------------------------------------- */

// ==================== KI-ÜBUNGEN (Szenarien + Zuordnungen) ====================
const ExercisePanel = () => {
  const { getAuthHeader } = useAuth();
  const [ov, setOv] = useState(null);
  const [starting, setStarting] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/interactive-exercises/overview`, getAuthHeader());
      setOv(r.data);
    } catch { /* noop */ }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 8000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!ov) return null;

  const jobRunning = ov.job && ['starting', 'running'].includes(ov.job.status);
  const remaining = ov.total_modules - ov.total_with_exercises;
  const pct = ov.total_modules > 0 ? Math.round((ov.total_with_exercises / ov.total_modules) * 100) : 0;

  const startAll = async () => {
    if (!window.confirm(
      `KI-Übungen für ${remaining} Module generieren?\n\n` +
      'Pro Modul entstehen 3 Fallszenarien + 2 Zuordnungen (~20 Sek./Modul, läuft im Hintergrund).\n' +
      'Module mit vorhandenen Übungen werden übersprungen.'
    )) return;
    setStarting(true);
    try {
      await axios.post(`${API}/admin/interactive-exercises/generate-all`, {}, getAuthHeader());
      toast.success('Übungs-Generierung gestartet — läuft im Hintergrund');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Start fehlgeschlagen');
    }
    setStarting(false);
  };

  return (
    <div className="bg-cream-100 rounded-2xl border border-cream-300 p-5 mb-6" data-testid="exercise-panel">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="font-mono text-[10px] uppercase font-semibold" style={{ letterSpacing: '0.22em', color: '#8F650F' }}>
            KI-Übungen · Szenarien &amp; Zuordnungen
          </p>
          <p className="text-[13.5px] text-espresso mt-1.5" data-testid="exercise-progress-text">
            <span className="font-display text-[20px]">{ov.total_with_exercises}</span>
            <span className="text-espresso/50"> von {ov.total_modules} Modulen haben Übungen ({pct} %)</span>
          </p>
        </div>
        {jobRunning ? (
          <div className="flex items-center gap-2.5 text-[12.5px]" style={{ color: '#E07A5F' }} data-testid="exercise-job-running">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>
              Generiert… {ov.job.done}/{ov.job.total}
              {ov.job.current ? ` · ${ov.job.current.substring(0, 34)}` : ''}
            </span>
          </div>
        ) : remaining > 0 ? (
          <button
            onClick={startAll}
            disabled={starting}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-[11.5px] font-bold uppercase transition-colors disabled:opacity-50"
            style={{ border: '1.5px solid #E07A5F', color: '#E07A5F', letterSpacing: '0.14em' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#E07A5F'; e.currentTarget.style.color = '#F5F0EA'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#E07A5F'; }}
            data-testid="generate-exercises-btn"
          >
            {starting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
            Übungen für alle Kurse generieren
          </button>
        ) : (
          <span className="inline-flex items-center gap-1.5 font-mono text-[10.5px] uppercase font-semibold" style={{ letterSpacing: '0.14em', color: '#8F650F' }}>
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#D89232' }} />
            Alle Module versorgt
          </span>
        )}
      </div>
      {/* Fortschrittslinie */}
      <div className="mt-3 h-[3px] rounded-full overflow-hidden" style={{ backgroundColor: '#E0D6CA' }}>
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: '#D89232' }} />
      </div>
      {ov.job?.status === 'error' && (
        <p className="text-[12px] mt-2" style={{ color: '#E07A5F' }}>Letzter Lauf abgebrochen: {ov.job.error} — einfach erneut starten (überspringt Fertiges).</p>
      )}
    </div>
  );
};

const AdminContentEnhancerPage = ({ Layout }) => {
  const { getAuthHeader } = useAuth();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeJobs, setActiveJobs] = useState([]);
  const [diffModuleId, setDiffModuleId] = useState(null);
  const [startingAll, setStartingAll] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  const loadOverview = async () => {
    try {
      const res = await axios.get(`${API}/admin/enhance/overview`, getAuthHeader());
      setOverview(res.data);
    } catch (e) {
      toast.error('Konnte Übersicht nicht laden');
    }
    setLoading(false);
  };

  const loadJobs = async () => {
    try {
      const res = await axios.get(`${API}/admin/enhance/jobs`, getAuthHeader());
      const running = (res.data.jobs || []).filter(j => j.status === 'starting' || j.status === 'running');
      // Also show most-recent 'done' or 'error' job for context
      const recentDone = (res.data.jobs || []).filter(j => j.status === 'done' || j.status === 'error').slice(0, 1);
      setActiveJobs([...running, ...recentDone]);
    } catch {
      // silent
    }
  };

  useEffect(() => {
    loadOverview();
    loadJobs();
    const t = setInterval(loadJobs, 5000);
    return () => clearInterval(t);
  }, [refreshTick]);

  const startAll = async () => {
    const modCount = overview?.total?.total_modules || '~300';
    if (!window.confirm(
      `${modCount} Module neu generieren?\n\n` +
      'Das überschreibt nichts Freigegebenes, dauert aber lange (1–2 Stunden).\n' +
      'Neue Inhalte landen im Status "Prüfung offen" und gehen erst nach deiner Freigabe live.'
    )) return;
    setStartingAll(true);
    try {
      const res = await axios.post(`${API}/admin/enhance/all/start?model=gpt-4o&force=false`, {}, getAuthHeader());
      if (res.data.status === 'already_running') {
        toast.info('Ein Job läuft bereits');
      } else {
        toast.success('Bulk-Enhancement gestartet');
      }
      setRefreshTick(t => t + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Konnte Job nicht starten');
    }
    setStartingAll(false);
  };

  const onJobRefresh = () => setRefreshTick(t => t + 1);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-terra" />
        </div>
      </Layout>
    );
  }
  if (!overview) return <Layout><div className="p-6">Keine Daten.</div></Layout>;

  const t = overview.total;
  const donePct = t.total_modules ? Math.round(100 * t.approved / t.total_modules) : 0;
  const anyRunning = activeJobs.some(j => j.status === 'running' || j.status === 'starting');

  return (
    <Layout>
      <div className="max-w-6xl mx-auto animate-fade-in">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <span className="w-8 h-[2px] bg-terra" />
            <span className="text-[11px] uppercase tracking-[0.25em] font-bold text-terra">System · KI-Inhalte</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-espresso leading-[1.05]">
            Content <span className="italic text-terra">Enhancer</span>.
          </h1>
          <p className="text-sand-600 mt-3 max-w-2xl">
            LLM-generierte Erweiterung der Modul-Inhalte (Faktor 5-10× Wörter). Jedes Kapitel geht erst live,
            wenn <strong>du</strong> es freigegeben hast. Bei medizinischen Inhalten liegt die fachliche
            Verantwortung bei dir.
          </p>
        </div>

        {/* Stats */}
        <div className="flex flex-wrap gap-3 mb-6">
          <StatCard label="Module gesamt" value={t.total_modules} testid="stat-total" />
          <StatCard label="Live" value={t.approved} hint={`${donePct}%`} color="terra" testid="stat-approved" />
          <StatCard label="Prüfung offen" value={t.pending} testid="stat-pending" />
          <StatCard label="Abgelehnt" value={t.rejected} testid="stat-rejected" />
          <StatCard label="Nicht generiert" value={t.missing} testid="stat-missing" />
          <StatCard label="Wörter LLM"
                    value={t.total_enhanced_words.toLocaleString('de-DE')}
                    hint={`Original: ${t.total_original_words.toLocaleString('de-DE')}`}
                    testid="stat-words" />
        </div>

        {/* Action bar */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <button
            onClick={() => { loadOverview(); loadJobs(); }}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-cream-100 text-espresso text-sm hover:bg-cream-200 border border-cream-300"
            data-testid="refresh-btn">
            <RefreshCw className="w-3.5 h-3.5" /> Aktualisieren
          </button>
          {anyRunning && (
            <span className="text-[12px] text-espresso/60 flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" /> Ein Job läuft – neuer Bulk-Start blockiert.
            </span>
          )}
        </div>

        {/* Active jobs */}
        {activeJobs.map(j => (
          <JobProgressCard
            key={j.job_id}
            jobId={j.job_id}
            onClose={() => setRefreshTick(t => t + 1)}
            onRefresh={onJobRefresh}
          />
        ))}

        {/* KI-Übungen */}
        <ExercisePanel />

        {/* Course list */}
        <div className="space-y-2.5">
          {overview.courses.map(c => (
            <CoursePanel
              key={c.course_id}
              course={c}
              onOpenDiff={(mid) => setDiffModuleId(mid)}
              onRefresh={onJobRefresh}
            />
          ))}
        </div>

        {/* Gefahrenzone: Bulk-Generierung — bewusst unten, Outline, mit Bestätigung */}
        <div className="mt-10 pt-6 border-t border-cream-300 flex items-center justify-between gap-4 flex-wrap">
          <p className="text-[12.5px] text-espresso/50 max-w-md">
            Generiert LLM-Inhalte für alle Kurse neu. Freigegebene Kapitel bleiben unberührt — der Lauf dauert aber lange.
          </p>
          <button
            onClick={startAll}
            disabled={startingAll || anyRunning}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[12px] font-bold uppercase transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ border: '1.5px solid #8A8175', color: '#8A8175', letterSpacing: '0.14em' }}
            onMouseEnter={(e) => { if (!e.currentTarget.disabled) { e.currentTarget.style.borderColor = '#E07A5F'; e.currentTarget.style.color = '#E07A5F'; } }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#8A8175'; e.currentTarget.style.color = '#8A8175'; }}
            data-testid="start-all-btn">
            {startingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
            Alle Kurse generieren
          </button>
        </div>
      </div>

      {diffModuleId && (
        <DiffViewer
          moduleId={diffModuleId}
          onClose={() => setDiffModuleId(null)}
          onActionDone={onJobRefresh}
        />
      )}
    </Layout>
  );
};

export default AdminContentEnhancerPage;
